/*
 * Тонкая обёртка над apiGetJSON с типизированными endpoint'ами.
 * При расширении (фильтры/пагинация/PATCH/DELETE) добавлять сюда.
 */
import { apiFetch, apiGetJSON, ApiError } from "./client";
import type {
  AppConfig,
  ListResponse,
  Recording,
  RecordingVisibility,
  SummariesResponse,
  UrlsResponse,
} from "../types";

/** Дефолты на случай если бэк недоступен или не отдаёт /config. Подобраны
 *  эмпирически на текущем железе (см. recorder-backend/.env.example).
 *  transcribe_overhead_sec=5 — только AI+complete, потому что worker
 *  репортит transcribe_started_at уже после ffmpeg. */
export const DEFAULT_CONFIG: AppConfig = {
  transcribe_heavy_rtf: 1.13,
  transcribe_overhead_sec: 5,
};

/** GET /api/v1/config — UX-константы. Никогда не бросает: при ошибке
 *  возвращает DEFAULT_CONFIG, чтобы UI не падал. */
export async function getAppConfig(): Promise<AppConfig> {
  try {
    return await apiGetJSON<AppConfig>("/v1/config");
  } catch {
    return DEFAULT_CONFIG;
  }
}

export interface ListParams {
  limit?: number;
  offset?: number;
  room_id?: string;
  /** 'element_call' | 'upload' — фильтр по типу записи. */
  source?: string;
  /** Manager-only фильтр "Загрузил" (Matrix user ID). Игнорируется для viewer'а. */
  started_by?: string;
  status?: string[]; // несколько через запятую
  q?: string; // search по title + room_display_name
  from?: string; // YYYY-MM-DD, включительно
  to?: string; // YYYY-MM-DD, включительно
  visibility?: string;
  tags?: string[];
}

export async function listRecordings(params: ListParams = {}): Promise<ListResponse> {
  const qs = new URLSearchParams();
  if (params.limit !== undefined) qs.set("limit", String(params.limit));
  if (params.offset !== undefined) qs.set("offset", String(params.offset));
  if (params.room_id) qs.set("room_id", params.room_id);
  if (params.source) qs.set("source", params.source);
  if (params.started_by) qs.set("started_by", params.started_by);
  if (params.status?.length) qs.set("status", params.status.join(","));
  if (params.q) qs.set("q", params.q);
  if (params.from) qs.set("from", params.from);
  if (params.to) qs.set("to", params.to);
  if (params.visibility) qs.set("visibility", params.visibility);
  for (const tag of params.tags ?? []) qs.append("tag", tag);
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return apiGetJSON<ListResponse>("/v1/recordings" + suffix);
}

export async function getRecordingUrls(id: string): Promise<UrlsResponse> {
  return apiGetJSON<UrlsResponse>(`/v1/recordings/${encodeURIComponent(id)}/urls`);
}

export interface RoomListItem {
  id: string;
  display_name?: string;
}

export interface RoomsListResponse {
  rooms: RoomListItem[];
}

export async function listRooms(): Promise<RoomsListResponse> {
  return apiGetJSON<RoomsListResponse>("/v1/rooms");
}

export async function getRecording(id: string): Promise<Recording> {
  return apiGetJSON<Recording>(`/v1/recordings/${encodeURIComponent(id)}`);
}

export async function setRecordingVisibility(id: string, expected: RecordingVisibility, visibility: RecordingVisibility, updated_at: string, tags: Record<string, number>): Promise<void> {
  const res = await apiFetch(`/v1/recordings/${encodeURIComponent(id)}/visibility`, {
    method: "PUT", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ expected, visibility, updated_at, tags }),
  });
  if (!res.ok) throw new ApiError(res.status, `Не удалось изменить доступ (${res.status})`);
}

export interface PatchPayload {
  title?: string;
  /** Полная замена acl_users (manager-владелец или admin). undefined → не трогаем. */
  acl_users?: string[];
  /** Переназначение владельца (ТОЛЬКО admin). MXID. undefined → не трогаем. */
  started_by?: string;
}

/** PATCH /api/v1/recordings/{id} — только manager. Возвращает обновлённую запись. */
export async function patchRecording(id: string, body: PatchPayload): Promise<Recording> {
  const res = await apiFetch(`/v1/recordings/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new ApiError(res.status, `PATCH ${id} ${res.status}: ${text}`);
  }
  return (await res.json()) as Recording;
}

/** Пользователь в подсказке autocomplete'а для шаринга записи. */
export interface AutocompleteUser {
  mxid: string;
  display_name?: string;
  email?: string;
}

export interface ListUsersResponse {
  users: AutocompleteUser[];
  has_more?: boolean;
}

/** GET /api/v1/users — manager-only autocomplete для шаринга. Никогда не
 *  бросает: при ошибке возвращает пустой список, фронт работает на ручном
 *  вводе MXID. */
export async function listUsers(q: string, limit = 20): Promise<AutocompleteUser[]> {
  try {
    const qs = new URLSearchParams();
    if (q) qs.set("q", q);
    if (limit) qs.set("limit", String(limit));
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    const res = await apiGetJSON<ListUsersResponse>("/v1/users" + suffix);
    return res.users || [];
  } catch {
    return [];
  }
}

/**
 * POST /api/v1/recordings/{id}/retranscribe — повторная транскрибация упавшей
 * записи. Доступно любому, кто видит запись (viewer с доступом или manager),
 * не только manager'у. diarize: true — полный режим (со спикерами), false —
 * без разделения по говорящим (легче по памяти). Возвращает обновлённую
 * запись (status станет 'assembled').
 */
export async function retranscribe(id: string, diarize: boolean): Promise<Recording> {
  const res = await apiFetch(`/v1/recordings/${encodeURIComponent(id)}/retranscribe`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ diarize }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new ApiError(res.status, `POST retranscribe ${id} ${res.status}: ${text}`);
  }
  return (await res.json()) as Recording;
}

/**
 * GET /api/v1/recordings/{id}/summaries — все варианты саммари (по табам).
 * Доступно любому, кто видит запись (viewer с доступом или manager).
 */
export async function getSummaries(id: string): Promise<SummariesResponse> {
  return apiGetJSON<SummariesResponse>(
    `/v1/recordings/${encodeURIComponent(id)}/summaries`,
  );
}

/**
 * POST /api/v1/recordings/{id}/resummarize — поставить запись в очередь на
 * (пере)генерацию саммари выбранным профилем. Транскрипт переиспользуется
 * (без повторной транскрибации). Доступно как просмотр записи. Возвращает
 * актуальный список вариантов (новый — в status='pending').
 */
export async function resummarize(
  id: string,
  profile: string,
): Promise<SummariesResponse> {
  const res = await apiFetch(
    `/v1/recordings/${encodeURIComponent(id)}/resummarize`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ profile }),
    },
  );
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new ApiError(res.status, `POST resummarize ${id} ${res.status}: ${text}`);
  }
  return (await res.json()) as SummariesResponse;
}

/**
 * PATCH /api/v1/recordings/{id}/summaries/{profile} — ручная правка текста
 * варианта саммари (внутри вкладки). MANAGER-ONLY. Возвращает обновлённый
 * список вариантов.
 */
export async function updateSummary(
  id: string,
  profile: string,
  summary: string,
): Promise<SummariesResponse> {
  const res = await apiFetch(
    `/v1/recordings/${encodeURIComponent(id)}/summaries/${encodeURIComponent(profile)}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ summary }),
    },
  );
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new ApiError(res.status, `PATCH summary ${id}/${profile} ${res.status}: ${text}`);
  }
  return (await res.json()) as SummariesResponse;
}

/**
 * DELETE /api/v1/recordings/{id}/summaries/{profile} — удалить один вариант
 * саммари (вкладку). Доступ — управление записью (admin или manager-владелец).
 * Возвращает обновлённый список вариантов (без удалённого).
 */
export async function deleteSummary(
  id: string,
  profile: string,
): Promise<SummariesResponse> {
  const res = await apiFetch(
    `/v1/recordings/${encodeURIComponent(id)}/summaries/${encodeURIComponent(profile)}`,
    { method: "DELETE" },
  );
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new ApiError(res.status, `DELETE summary ${id}/${profile} ${res.status}: ${text}`);
  }
  return (await res.json()) as SummariesResponse;
}

/** DELETE /api/v1/recordings/{id} — только manager. */
export async function deleteRecording(id: string): Promise<void> {
  const res = await apiFetch(`/v1/recordings/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new ApiError(res.status, `DELETE ${id} ${res.status}: ${text}`);
  }
}
